import React, { useMemo } from 'react';
import { ImageProps } from '@components/image';
import { RichTextBlock } from 'prismic-reactjs';
import Wrapper from '@components/wrapper.styled';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import AnimatedElement from '@components/animated-element';
import {
  Header,
  Container,
  Content,
  ImageContainer,
  StyledSubheading,
  TextContainer,
  SmallCopy,
  StyledImage,
  LinkButton,
} from './page-hero.styled';

export interface PageHeroProps {
  image?: ImageProps;
  imageFit?: boolean;
  title?: string;
  subheading?: RichTextBlock[];
  description?: RichTextBlock[];
  linkLabel?: string;
  link?: {
    url?: string;
    uid?: string;
    target?: string;
  };
}

const PageHero = ({
  image,
  imageFit = false,
  title,
  subheading = [],
  description = [],
  linkLabel,
  link,
}: PageHeroProps): JSX.Element => {
  const linkConfig = useMemo(() => getLinkConfig(link), [link]);
  const hasSubheading: boolean = subheading?.length > 0 && subheading[0].text !== '';

  if (!title && !image && !hasSubheading) {
    return <></>;
  }

  return (
    <Header className="page-hero">
      <Wrapper>
        {image?.url ? (
          <Container>
            <Content>
              {title && (
                <Text
                  fontSize="heading2"
                  type="title"
                  stringText={title}
                  fontColor="white"
                  margin="none"
                />
              )}
              {hasSubheading && (
                <StyledSubheading
                  type="subheading"
                  richText={subheading}
                  fontColor="white"
                  fontSize="body"
                />
              )}
              {linkConfig.url !== '' && (
                <LinkButton
                  type="button"
                  url={linkConfig.url}
                  title={linkLabel || 'Learn More'}
                  isExternal={linkConfig.isExternal}
                  target={link?.target || '_self'}
                  aria-label={linkLabel || 'Learn More'}
                />
              )}
              {description && (
                <SmallCopy>
                  <Text
                    fontSize="small"
                    type="description"
                    richText={description}
                    fontColor="white"
                  />
                </SmallCopy>
              )}
            </Content>
            <ImageContainer>
              <AnimatedElement
                animationType="slideInRight"
                delay={0.125}
                threshold={0.125}
                rootMargin="0px"
              >
                <StyledImage
                  gatsbyImageData={image.gatsbyImageData}
                  url={image.url}
                  alt={image.alt}
                  objectFit={imageFit ? 'cover' : 'contain'}
                  imageFit={imageFit}
                />
              </AnimatedElement>
            </ImageContainer>
          </Container>
        ) : (
          <TextContainer>
            {title && <Text fontSize="heading1" type="title" stringText={title} />}
            {hasSubheading && <StyledSubheading type="subheading" richText={subheading} />}
          </TextContainer>
        )}
      </Wrapper>
    </Header>
  );
};

export default PageHero;
